import React, { useMemo } from 'react'
import { HEX_COLORS, HEX_SOLID, equilateralHexPath, hexGradientId, type HexTone } from './hexTheme'

interface HexShapeProps {
  tone: HexTone
  /** Flat-to-flat width of the equilateral pointy-top hex. */
  width: number
  opacity?: number
  gradKey: string
  style?: React.CSSProperties
  className?: string
  stroke?: boolean
  /** Solid flat fill (expanded inner stats) vs radial gradient (default). */
  solid?: boolean
  /** Override solid / gradient fill (e.g. brand top-cap colours). */
  fillColor?: string
  /**
   * `layer` — brand radial: slate core → severity rim at 50%.
   * `expanded` — softer centred fade used on expanded background hexes.
   */
  gradient?: 'layer' | 'expanded'
}

export function HexShape({
  tone,
  width,
  opacity = 1,
  gradKey,
  style,
  className,
  stroke = true,
  solid = false,
  fillColor,
  gradient = 'layer',
}: HexShapeProps) {
  const { rim, core } = HEX_COLORS[tone]
  const { path, height, vb } = useMemo(() => equilateralHexPath(width), [width])
  const gid = hexGradientId(tone, `${gradKey}-${gradient}`)
  const solidFill = fillColor ?? HEX_SOLID[tone]
  const fill = solid || fillColor ? solidFill : `url(#${gid})`
  const useGradient = !solid && !fillColor

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', opacity, ...style }}
      aria-hidden
    >
      {useGradient && (
        <defs>
          {gradient === 'expanded' ? (
            <radialGradient id={gid} cx="50%" cy="50%" r="97%" fx="50%" fy="50%">
              <stop offset="0%" stopColor={core} />
              <stop offset="50%" stopColor={rim} />
              <stop offset="100%" stopColor={rim} />
            </radialGradient>
          ) : (
            // Brand spec: radial-gradient(circle at 50% 50%, slate 0%, severity/grey 50%)
            <radialGradient id={gid} cx="50%" cy="50%" r="100%" fx="50%" fy="50%">
              <stop offset="0%" stopColor={core} />
              <stop offset="50%" stopColor={rim} />
              <stop offset="100%" stopColor={rim} />
            </radialGradient>
          )}
        </defs>
      )}
      <path
        d={path}
        fill={fill}
        stroke={stroke ? (solid || fillColor ? 'rgba(255,255,255,0.18)' : rim) : 'none'}
        strokeWidth={stroke ? (solid || fillColor ? 1 : 1.2) : 0}
        strokeOpacity={solid || fillColor ? 1 : 0.85}
      />
    </svg>
  )
}
